// Track an order — real guest lookup (order number + phone, no login).
//
// Calls the lumus-track-order-api Lambda (see that folder for source/deploy
// notes — it's not part of this theme repo) with { order_number, phone } and
// renders whatever it returns. Nothing here is invented: if the API has no
// tracking_url for a fulfillment yet, no live-tracking panel is shown; if it
// has no fulfillment-event history, the journey stepper falls back to the
// same coarse Processing/Shipped/Cancelled/Refunded labels
// sections/order-history.liquid already uses, not fabricated granularity.

const STATUS_COPY = {
  active: { line: "We're preparing your order", sub: 'Tracking appears here as soon as it ships.' },
  completed: { line: 'Your order has shipped', sub: 'Track the live status below.' },
  cancelled: { line: 'Order cancelled', sub: 'A refund will be initiated if applicable.' },
  refunded: { line: 'Order refunded', sub: 'The refund has been processed.' },
};

// Shopify Admin's FulfillmentEvent.status enum — only shown if the store's
// tracking integration actually pushes these via the Fulfillment Event API.
const EVENT_LABELS = {
  CONFIRMED: 'Confirmed',
  LABEL_PURCHASED: 'Label purchased',
  LABEL_PRINTED: 'Label printed',
  READY_FOR_PICKUP: 'Ready for pickup',
  PICKED_UP: 'Picked up',
  IN_TRANSIT: 'In transit',
  OUT_FOR_DELIVERY: 'Out for delivery',
  ATTEMPTED_DELIVERY: 'Delivery attempted',
  DELIVERED: 'Delivered',
  NOT_DELIVERED: 'Not delivered',
  DELAYED: 'Delayed',
  FAILURE: 'Delivery failed',
  CANCELLED: 'Cancelled',
};

function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtMoney(amount, currency) {
  if (amount == null || amount === '') return '';
  const symbol = currency === 'INR' ? '₹' : currency ? currency + ' ' : '';
  return symbol + amount;
}

// Inlined rather than fetched — this file has no Liquid `inline_asset_content`
// filter to reach for, and it's just two small paths.
const CHECK_ICON = '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CLOSE_ICON = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.1765 12.025C11.4108 12.2594 11.7907 12.2594 12.025 12.025C12.2594 11.7907 12.2594 11.4108 12.025 11.1765L8.84931 8.00078L12.025 4.82505C12.2594 4.59073 12.2594 4.21083 12.025 3.97652C11.7907 3.7422 11.4108 3.7422 11.1765 3.97652L8.00078 7.15225L4.82505 3.97652C4.59073 3.7422 4.21083 3.7422 3.97652 3.97652C3.7422 4.21083 3.7422 4.59073 3.97652 4.82505L7.15225 8.00078L3.97652 11.1765C3.7422 11.4108 3.7422 11.7907 3.97652 12.025C4.21083 12.2594 4.59073 12.2594 4.82505 12.025L8.00078 8.84931L11.1765 12.025Z" fill="currentColor"/></svg>';

// Statuses that end the journey without a delivery — shown in the error
// color with an × instead of the green checkmark "done" steps get, so a
// cancelled/failed order doesn't read as a successful milestone.
const NEGATIVE_STATUSES = new Set(['CANCELLED', 'NOT_DELIVERED', 'FAILURE']);

function buildSteps(order) {
  const events = (order.fulfillments || []).flatMap((f) => f.events || []);

  if (events.length) {
    const sorted = events.slice().sort((a, b) => new Date(a.happened_at) - new Date(b.happened_at));
    return sorted.map((event, index) => {
      const isLast = index === sorted.length - 1;
      // DELIVERED/cancelled/failed end the journey rather than staying
      // "in progress" forever — without this the last event rendered as an
      // empty "current" dot even once the order had already arrived (or
      // stopped moving for good).
      const isFinal = isLast && event.status === 'DELIVERED';
      const isNegative = isLast && NEGATIVE_STATUSES.has(event.status);
      return {
        label: EVENT_LABELS[event.status] || event.status,
        note: fmtDate(event.happened_at),
        done: !isLast || isFinal || isNegative,
        current: isLast && !isFinal && !isNegative,
        negative: isNegative,
      };
    });
  }

  const steps = [{ label: 'Order placed', done: true }];
  if (order.status_bucket === 'cancelled') {
    steps.push({ label: 'Cancelled', done: true, current: true, negative: true });
  } else if (order.status_bucket === 'refunded') {
    steps.push({ label: 'Refunded', done: true, current: true, negative: true });
  } else if (order.status_bucket === 'completed') {
    steps.push({ label: 'Shipped', done: true, current: true });
  } else {
    steps.push({ label: 'Processing', current: true, note: "We'll update this as soon as it ships." });
  }
  return steps;
}

function stepsHTML(order) {
  return buildSteps(order)
    .map((step) => {
      const icon = step.done ? (step.negative ? CLOSE_ICON : CHECK_ICON) : '';
      return `
        <div class="tor-step${step.done ? ' is-done' : ''}${step.current ? ' is-current' : ''}${step.negative ? ' is-negative' : ''}">
          <div class="tor-step__dot">${icon}</div>
          <div>
            <div class="tor-step__label">${esc(step.label)}</div>
            ${step.note ? `<div class="tor-step__note">${esc(step.note)}</div>` : ''}
          </div>
        </div>`;
    })
    .join('');
}

function fulfillmentPanelHTML(fulfillment, index) {
  if (!fulfillment.tracking_url && !fulfillment.tracking_company && !fulfillment.tracking_number) return '';

  return `
    <div class="tor-panel">
      <div class="tor-panel__head">
        <div class="tor-panel__head-left">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M1 3h13v13H1z"></path><path d="M14 8h4l3 3v5h-7z"></path><circle cx="5.5" cy="18.5" r="2"></circle><circle cx="17.5" cy="18.5" r="2"></circle></svg>
          Shipment ${index + 1}
        </div>
        ${fulfillment.tracking_url ? `<a href="${esc(fulfillment.tracking_url)}" target="_blank" rel="noopener" class="tor-panel__open">Open ↗</a>` : ''}
      </div>
      <div class="tor-courier-row">
        ${fulfillment.tracking_company ? `<span class="tor-courier-pill">${esc(fulfillment.tracking_company)}</span>` : ''}
        ${
          fulfillment.tracking_number
            ? `<span class="tor-awb">AWB <strong>${esc(fulfillment.tracking_number)}</strong></span>
               <button type="button" class="tor-copy-btn" data-tor-copy="${esc(fulfillment.tracking_number)}">Copy</button>`
            : ''
        }
      </div>
      ${
        fulfillment.tracking_url
          ? `
        <div class="tor-iframe-box">
          <iframe src="${esc(fulfillment.tracking_url)}" loading="lazy" title="Live courier tracking — Shipment ${index + 1}" referrerpolicy="no-referrer-when-downgrade"></iframe>
        </div>
        <div class="tor-iframe-fallback">Not loading? <a href="${esc(fulfillment.tracking_url)}" target="_blank" rel="noopener">Open on courier site ↗</a></div>
      `
          : ''
      }
    </div>
  `;
}

function itemsHTML(items) {
  return items
    .map(
      (item) => `
        <div class="tor-item-row">
          ${
            item.image_url
              ? `<img class="tor-item-thumb" src="${esc(item.image_url)}" alt="${esc(item.title)}" loading="lazy" width="52" height="52">`
              : '<span class="tor-item-thumb"></span>'
          }
          <div class="tor-item-info">
            <div class="tor-item-name">${esc(item.title)}</div>
            ${item.sku ? `<div class="tor-item-sku">SKU: ${esc(item.sku)}</div>` : ''}
          </div>
          <div class="tor-item-price-col">
            <div class="tor-item-price">${esc(fmtMoney(item.price, item.currency))}</div>
            <div class="tor-item-qty">Qty ${item.quantity || 1}</div>
          </div>
        </div>`
    )
    .join('');
}

function addressHTML(address) {
  if (!address) return '';
  const street = [address.line1, address.line2].filter(Boolean).map(esc).join(', ');
  const cityLine = [address.city, address.province, address.zip].filter(Boolean).map(esc).join(', ');
  return [street, cityLine].filter(Boolean).join('<br>');
}

function buildResultHTML(order, supportEmail) {
  const bucket = order.status_bucket;
  const copy = STATUS_COPY[bucket] || STATUS_COPY.active;
  const address = addressHTML(order.shipping_address);
  const fulfillments = (order.fulfillments || []).map(fulfillmentPanelHTML).filter(Boolean).join('');
  const hasAnyTrackingUrl = (order.fulfillments || []).some((f) => f.tracking_url);
  const items = order.line_items || [];

  return `
    <div class="tor-hero" data-status-bucket="${esc(bucket)}">
      <div class="tor-hero__top">
        <div>
          <div class="tor-hero__badge">${esc(order.status_label)}</div>
          <div class="tor-hero__line">${esc(copy.line)}</div>
          <div class="tor-hero__sub">${esc(copy.sub)}</div>
        </div>
        <div>
          <button type="button" class="tor-hero__id-btn" data-tor-copy="${esc(order.name)}">
            ${esc(order.name)}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
          </button>
          <div class="tor-hero__placed">Placed ${esc(fmtDate(order.placed_at))}</div>
        </div>
      </div>
      <div class="tor-hero__bottom">
        <div class="tor-hero__col">
          <div class="tor-hero__micro">Recipient</div>
          <div>${esc(order.customer_name)}</div>
        </div>
        ${
          address
            ? `<div class="tor-hero__col" style="flex:2;">
                 <div class="tor-hero__micro">Shipping address</div>
                 <div>${address}</div>
               </div>`
            : ''
        }
      </div>
    </div>

    ${fulfillments}

    ${
      !hasAnyTrackingUrl
        ? `<div class="tor-panel">
             <div class="tor-panel__head"><div class="tor-panel__head-left">Order journey</div></div>
             <div class="tor-steps">${stepsHTML(order)}</div>
           </div>`
        : ''
    }

    <div class="tor-items-head">
      <div class="tor-items-head-title">Your items</div>
      <span class="tor-items-count">${items.length} item${items.length === 1 ? '' : 's'}</span>
    </div>
    <div class="tor-ship">${itemsHTML(items)}</div>

    <div class="tor-support">
      <div>
        <div class="tor-support-title">Something not right?</div>
        <div class="tor-support-sub">Our team replies within minutes.</div>
      </div>
      ${supportEmail ? `<a href="mailto:${esc(supportEmail)}" class="tor-support-cta">Email support</a>` : ''}
    </div>
  `;
}

function bindResultInteractions(root) {
  root.querySelectorAll('[data-tor-copy]').forEach((button) => {
    button.addEventListener('click', () => {
      const value = button.dataset.torCopy;
      if (navigator.clipboard) navigator.clipboard.writeText(value).catch(() => {});
      const original = button.innerHTML;
      button.textContent = 'Copied!';
      setTimeout(() => {
        button.innerHTML = original;
      }, 1400);
    });
  });
}

class TrackOrderApp extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.endpoint = this.dataset.endpoint || '';
    this.apiKey = this.dataset.apiKey || '';
    this.supportEmail = this.dataset.supportEmail || '';

    this.views = {
      form: this.querySelector('[data-track-order-view="form"]'),
      loading: this.querySelector('[data-track-order-view="loading"]'),
      result: this.querySelector('[data-track-order-view="result"]'),
    };

    this.form = this.querySelector('[data-track-order-form]');
    this.orderInput = this.querySelector('[data-track-order-number]');
    this.phoneInput = this.querySelector('[data-track-order-phone]');
    this.note = this.querySelector('[data-track-order-note]');
    this.resultContent = this.querySelector('[data-track-order-result-content]');
    this.againButton = this.querySelector('[data-track-order-again]');

    this.form?.addEventListener('submit', this.#handleSubmit, { signal });
    this.againButton?.addEventListener('click', this.#reset, { signal });

    this.#restoreFromQueryParams();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  // Lets the "Track order" button on /pages/order-history send someone
  // straight to a result: it already knows their real order number + phone,
  // so this pre-fills both fields and submits the real form the normal way
  // — no separate fetch path to keep in sync with #handleSubmit. The
  // Kwikpass-sourced order list doesn't always carry a phone number, so
  // `phone` is optional: with no phone this still pre-fills the order number
  // and leaves submitting to the visitor, rather than doing nothing.
  #restoreFromQueryParams() {
    const params = new URLSearchParams(window.location.search);
    const order = params.get('order');
    const phone = params.get('phone');
    if (!order || !this.orderInput || !this.phoneInput || !this.form) return;

    this.orderInput.value = order;
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);

    if (!phone) return;

    this.phoneInput.value = phone;
    this.form.requestSubmit();
  }

  #showView(name) {
    for (const [key, el] of Object.entries(this.views)) {
      if (el) el.hidden = key !== name;
    }
  }

  #showNote(message, state) {
    if (!this.note) return;
    this.note.textContent = message;
    this.note.hidden = false;
    if (state) this.note.dataset.state = state;
    else delete this.note.dataset.state;
  }

  #hideNote() {
    if (!this.note) return;
    this.note.hidden = true;
    delete this.note.dataset.state;
  }

  #reset = () => {
    this.form?.reset();
    this.#hideNote();
    this.#showView('form');
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  #handleSubmit = async (event) => {
    event.preventDefault();
    this.#hideNote();

    const orderNumber = (this.orderInput?.value || '').trim();
    const phone = (this.phoneInput?.value || '').trim();

    if (!orderNumber || !phone) {
      this.#showNote('Enter both your order number and mobile number.', 'error');
      return;
    }

    if (!this.endpoint) {
      this.#showNote("Tracking isn't connected yet — please check back soon, or reach support below.", 'error');
      return;
    }

    this.#showView('loading');

    const headers = { 'Content-Type': 'application/json' };
    if (this.apiKey) headers.Authorization = `Bearer ${this.apiKey}`;

    let data;
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ order_number: orderNumber, phone }),
      });
      data = await response.json();
    } catch {
      this.#showView('form');
      this.#showNote("We couldn't reach our tracking service. Please try again in a moment.", 'error');
      return;
    }

    if (data.status === 'ok') {
      if (this.resultContent) {
        this.resultContent.innerHTML = buildResultHTML(data.order, this.supportEmail);
        bindResultInteractions(this.resultContent);
      }
      this.#showView('result');
      return;
    }

    this.#showView('form');
    if (data.status === 'not_found') {
      this.#showNote("We couldn't find an order with that number and phone. Double-check both and try again.", 'error');
    } else {
      this.#showNote('Something went wrong on our end. Please try again.', 'error');
    }
  };
}

if (!customElements.get('track-order-app')) {
  customElements.define('track-order-app', TrackOrderApp);
}
